import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowRight, MessageCircle, ShieldCheck, Sparkles, Star, Award, MapPin, ChevronDown, Briefcase, Wallet } from "lucide-react";
import { CreceLogo } from "@/components/CreceLogo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FAQS = [
  {
    q: "How much can I earn?",
    a: "There's no cap. Most active consultants earn ₹15–40k in their first 3 months by sharing credit cards, loans and insurance. Top performers cross ₹1 lakh/month within a year.",
  },
  {
    q: "Do I need any investment?",
    a: "No. Crece is 100% free to join. You only need a phone and an internet connection to start sharing products and earning commission.",
  },
  {
    q: "When and how do I get paid?",
    a: "Commission is added to your wallet the moment a lead is marked Paid. You can withdraw anytime to your bank account via NEFT (typically credited within 24–48 hours).",
  },
  {
    q: "Is my data safe?",
    a: "Yes. Crece is built on bank-grade security (HTTPS, encrypted storage, OTP verification). Your KYC details are only used for payout compliance.",
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-stone-200 last:border-0 py-4">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left gap-4" data-testid="ref-faq-toggle">
        <span className="font-display font-bold text-base text-stone-900">{q}</span>
        <ChevronDown className={`w-5 h-5 text-stone-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="text-sm text-stone-600 mt-3 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function ReferralLanding() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const c = (code || "").trim().toUpperCase();
    if (!c) { setError("Invalid referral code"); return; }
    axios.get(`${BACKEND}/api/r/${c}`)
      .then((r) => setPartner(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Referral not found"));
  }, [code]);

  const onJoin = () => {
    navigate(`/auth?ref=${encodeURIComponent(code.toUpperCase())}&mode=signup&utm_source=referral_landing&utm_campaign=r_${code.toUpperCase()}`);
  };

  const waNumber = partner?.whatsapp_number ? `91${partner.whatsapp_number}` : "";
  const waMsg = encodeURIComponent(`Hi ${partner?.name || "there"}, I came via your Crece referral page (${code}) and want to know more about joining as a financial consultant.`);
  const waUrl = waNumber ? `https://wa.me/${waNumber}?text=${waMsg}` : "";

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center p-6" data-testid="ref-error">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-100 mx-auto grid place-items-center mb-4">
            <Sparkles className="w-7 h-7 text-red-600" />
          </div>
          <h1 className="font-display text-2xl font-black">Referral not found</h1>
          <p className="text-stone-500 mt-2 text-sm">{error}</p>
          <Link to="/auth"><Button className="mt-6 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold">Continue to sign up</Button></Link>
        </div>
      </div>
    );
  }

  if (!partner) {
    return <div className="min-h-screen bg-stone-50 grid place-items-center text-stone-400">Loading…</div>;
  }

  const initials = (partner.name || "").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-stone-50" data-testid="referral-landing">
      {/* Top bar */}
      <header className="px-5 sm:px-8 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <CreceLogo size="sm" theme="dark" />
          <span className="font-display font-black text-xl tracking-tight">Crece</span>
        </Link>
        <Link to="/auth" className="text-sm font-bold text-emerald-900 hover:underline" data-testid="ref-existing-login">Already a member? Sign in</Link>
      </header>

      {/* Hero */}
      <section className="px-5 sm:px-8 pt-4 pb-12">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[1fr_440px] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-emerald-900/5 border border-emerald-900/10 text-emerald-900 text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full">
              <Sparkles className="w-3.5 h-3.5" /> Referral · {partner.referral_code}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-5 leading-[1.05]">
              Join <span className="text-emerald-900">{partner.name.split(" ")[0]}</span>{"'"}s team on <span className="bg-amber-200/70 px-2 rounded">Crece</span>.
            </h1>
            <p className="text-stone-600 mt-5 text-base sm:text-lg max-w-xl leading-relaxed">
              Earn weekly by referring credit cards, loans, insurance and savings accounts from India{"'"}s leading banks. No investment, no targets — just a phone and a network.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Button onClick={onJoin} data-testid="ref-join-cta"
                className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold h-12 px-7 shadow-lg shadow-emerald-900/20">
                Join free in 30 seconds <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              {waUrl && (
                <a href={waUrl} target="_blank" rel="noreferrer" data-testid="ref-whatsapp-cta">
                  <Button variant="outline" className="rounded-full border-2 border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 font-bold h-12 px-6">
                    <MessageCircle className="w-4 h-4 mr-2" /> Chat on WhatsApp
                  </Button>
                </a>
              )}
            </div>
            {/* Trust strip */}
            <div className="grid grid-cols-3 gap-4 mt-10 max-w-xl">
              <div className="text-center" data-testid="ref-trust-banks">
                <div className="font-display text-2xl font-black text-emerald-900">15+</div>
                <div className="text-xs text-stone-500 uppercase tracking-wider mt-1">Top banks</div>
              </div>
              <div className="text-center">
                <div className="font-display text-2xl font-black text-emerald-900">₹0</div>
                <div className="text-xs text-stone-500 uppercase tracking-wider mt-1">Joining fee</div>
              </div>
              <div className="text-center">
                <div className="font-display text-2xl font-black text-emerald-900">24h</div>
                <div className="text-xs text-stone-500 uppercase tracking-wider mt-1">Weekly payouts</div>
              </div>
            </div>
          </div>

          {/* Partner card */}
          <div className="bg-gradient-to-br from-emerald-900 to-emerald-950 text-white rounded-3xl p-7 shadow-xl shadow-emerald-900/20 relative overflow-hidden" data-testid="ref-partner-card">
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-amber-500/20 rounded-full blur-3xl" />
            <div className="relative">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">{partner.is_pl_head ? "P&L Head · Your sponsor" : "Your sponsor"}</div>
              <div className="flex items-center gap-4 mt-4">
                <div className="w-20 h-20 rounded-2xl overflow-hidden border-2 border-amber-400/30 bg-stone-700 grid place-items-center shrink-0">
                  {partner.photo_base64 ? (
                    <img src={partner.photo_base64} alt={partner.name} className="w-full h-full object-cover" data-testid="ref-partner-photo" />
                  ) : (
                    <span className="font-display text-2xl font-black text-amber-400" data-testid="ref-partner-initials">{initials || "C"}</span>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-display text-2xl font-black truncate" data-testid="ref-partner-name">{partner.name}</div>
                  {partner.designation && (
                    <div className="inline-flex items-center gap-1.5 mt-1.5 bg-amber-400/15 border border-amber-400/40 text-amber-300 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider" data-testid="ref-partner-designation">
                      <Award className="w-3 h-3" /> {partner.designation}
                    </div>
                  )}
                  {/* "Recently promoted" celebration pill — shown for ~30 days after each rank-up. */}
                  {partner.promoted_at && Date.now() - new Date(partner.promoted_at).getTime() < 30 * 24 * 60 * 60 * 1000 && (
                    <div className="inline-flex items-center gap-1.5 mt-1.5 ml-1.5 bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider" data-testid="ref-partner-promoted">
                      🎉 Recently promoted · {new Date(partner.promoted_at).toLocaleDateString("en-IN", { month: "short", day: "numeric" })}
                    </div>
                  )}
                  {partner.city && (
                    <div className="flex items-center gap-1 text-stone-300 text-sm mt-1">
                      <MapPin className="w-3.5 h-3.5" /> {partner.city}
                    </div>
                  )}
                </div>
              </div>

              {partner.bio && <p className="text-stone-200 text-sm mt-5 leading-relaxed" data-testid="ref-partner-bio">{partner.bio}</p>}

              {/* Trust badges */}
              <div className="flex flex-wrap gap-2 mt-5">
                {partner.years_experience > 0 && (
                  <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur border border-white/20 px-3 py-1.5 rounded-full text-xs font-semibold" data-testid="ref-badge-exp">
                    <Award className="w-3.5 h-3.5 text-amber-400" /> {partner.years_experience}+ yrs experience
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur border border-white/20 px-3 py-1.5 rounded-full text-xs font-semibold">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" /> Verified consultant
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur border border-white/20 px-3 py-1.5 rounded-full text-xs font-semibold">
                  <Star className="w-3.5 h-3.5 text-amber-400" /> {partner.is_pl_head ? "Crece P&L Head" : "Crece partner"}
                </span>
              </div>

              {/* Specialties */}
              {(partner.specialties || []).length > 0 && (
                <div className="mt-6">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400/80 mb-2">Specialises in</div>
                  <div className="flex flex-wrap gap-1.5" data-testid="ref-partner-specialties">
                    {partner.specialties.map((s) => (
                      <span key={s} className="bg-amber-400/15 text-amber-200 border border-amber-400/30 px-2.5 py-1 rounded-full text-xs font-semibold">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              <Button onClick={onJoin} data-testid="ref-join-cta-card"
                className="w-full mt-7 rounded-full bg-amber-500 hover:bg-amber-600 text-emerald-950 font-black h-12">
                Join {partner.name.split(" ")[0]}{"'"}s team <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why join */}
      <section className="bg-white border-y border-stone-200 px-5 sm:px-8 py-14">
        <div className="max-w-5xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-800">Why Crece</span>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-3 max-w-2xl">A career plan built for financial consultants.</h2>
          <div className="grid sm:grid-cols-3 gap-5 mt-9">
            {[
              { icon: Wallet, t: "Earn weekly", d: "Commissions are added to your wallet instantly when leads pay. Withdraw to your bank via NEFT anytime." },
              { icon: Briefcase, t: "15+ banks, 100s of products", d: "Pick from credit cards, loans, savings, demat & insurance. Real bank partnerships, not random offers." },
              { icon: Award, t: "10-grade career plan", d: "Build a team, climb ranks, and unlock contests, perks and override commissions on every grade." },
            ].map((b) => (
              <div key={b.t} className="bg-stone-50 border border-stone-200 rounded-2xl p-6">
                <div className="w-11 h-11 rounded-xl bg-emerald-900 text-white grid place-items-center"><b.icon className="w-5 h-5" /></div>
                <div className="font-display text-lg font-black mt-4">{b.t}</div>
                <p className="text-sm text-stone-600 mt-2 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-5 sm:px-8 py-14">
        <div className="max-w-3xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-800">FAQ</span>
          <h2 className="font-display text-3xl font-black tracking-tight mt-3">Questions before you join?</h2>
          <div className="mt-6 bg-white rounded-2xl border border-stone-200 px-6">
            {FAQS.map((f) => <FaqItem key={f.q} {...f} />)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-5 sm:px-8 pb-16">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-amber-50 to-amber-100/60 border-2 border-amber-300 rounded-3xl p-9 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Ready to start earning?</h2>
          <p className="text-stone-700 mt-3 max-w-xl mx-auto">Join {partner.name}{"'"}s team today and we{"'"}ll show you exactly how to earn your first ₹5,000 in 7 days — step-by-step.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-7">
            <Button onClick={onJoin} data-testid="ref-final-join-cta"
              className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-black h-12 px-7">
              Create my free account <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            {waUrl && (
              <a href={waUrl} target="_blank" rel="noreferrer">
                <Button variant="outline" className="rounded-full border-2 border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 font-bold h-12 px-6">
                  <MessageCircle className="w-4 h-4 mr-2" /> Ask on WhatsApp
                </Button>
              </a>
            )}
          </div>
        </div>
      </section>

      <footer className="px-5 sm:px-8 py-8 text-center text-xs text-stone-500">
        © Crece · Career Plan for Financial Consultants · You{"'"}ll be signing up under {partner.name} ({partner.referral_code}).
      </footer>
    </div>
  );
}
